import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Phone, PhoneIncoming, PhoneOff } from 'lucide-react';
import { startRinging } from './sound';

/** One voice call as the server describes it. */
export interface CallInfo { id: string; status: string; startedBy: 'client' | 'staff'; offer: string; answer: string | null; remoteIce: RTCIceCandidateInit[]; staffName?: string | null; answeredAt?: string | null; relay?: boolean }

export interface CallApi {
  side: 'client' | 'staff';
  iceServers: () => Promise<RTCIceServer[]>;
  current: () => Promise<CallInfo | null>;
  get: (id: string) => Promise<CallInfo>;
  start: (offer: string) => Promise<{ id: string }>;
  answer: (id: string, sdp: string) => Promise<unknown>;
  ice: (id: string, c: RTCIceCandidateInit) => Promise<unknown>;
  end: (id: string) => Promise<unknown>;
  /** Send what this browser saw during the call, for diagnosing failed calls. */
  diag?: (id: string, events: string[]) => Promise<unknown>;
  /** WebSocket address of the server audio relay, used when no direct route connects. */
  relayUrl?: (id: string) => string;
  /** Staff side: upload the recording of both voices. */
  saveRecording?: (id: string, file: Blob) => Promise<unknown>;
}

type Phase = 'idle' | 'incoming' | 'calling' | 'connecting' | 'active' | 'reconnecting';

/** Browser-to-browser voice calls over WebRTC, with signalling through the CK API. */
export function useVoiceCall(api: CallApi, enabled = true) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [incoming, setIncoming] = useState<CallInfo | null>(null);
  const pc = useRef<RTCPeerConnection | null>(null);
  const local = useRef<MediaStream | null>(null);
  const remote = useRef<MediaStream | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const callId = useRef<string | null>(null);
  const pendingIce = useRef<RTCIceCandidateInit[]>([]);
  const iceSeen = useRef(0);
  const answered = useRef(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const mix = useRef<AudioContext | null>(null);
  const timer = useRef<number | null>(null);
  const relay = useRef<{ ws: WebSocket; ctx: AudioContext; node: ScriptProcessorNode; src: MediaStreamAudioSourceNode; next: number } | null>(null);
  const relayTimer = useRef<number | null>(null);
  const mutedRef = useRef(false);
  const log = useRef<string[]>([]);
  const note = (t: string) => { log.current.push(`${new Date().toISOString().slice(11, 19)} ${t}`); if (log.current.length > 70) log.current.shift(); };
  const sendDiag = () => { const id = callId.current; if (id && api.diag && log.current.length) void api.diag(id, log.current.slice()).catch(() => undefined); };

  /**
   * Server relay: when the direct route does not connect within a few seconds, both sides send their audio
   * through the CK server over a WebSocket. Works on any network that can open the site.
   */
  async function startRelay() {
    const id = callId.current;
    if (!id || relay.current || !api.relayUrl) return;
    note('switching to server relay');
    if (relayTimer.current) { clearTimeout(relayTimer.current); relayTimer.current = null; }
    try { pc.current?.close(); } catch { /* closed */ }
    pc.current = null;
    if (!local.current) local.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    void ctx.resume();
    const src = ctx.createMediaStreamSource(local.current);
    const node = ctx.createScriptProcessor(2048, 1, 1);
    const out = ctx.createMediaStreamDestination();
    const ratio = ctx.sampleRate / 16000;
    const ws = new WebSocket(api.relayUrl(id));
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => note('relay connected');
    ws.onerror = () => note('relay error');
    let heard = 0;
    const r = { ws, ctx, node, src, next: 0 };
    relay.current = r;
    node.onaudioprocess = (e) => {
      e.outputBuffer.getChannelData(0).fill(0);
      if (ws.readyState !== 1 || mutedRef.current) return;
      const inp = e.inputBuffer.getChannelData(0);
      const n = Math.floor(inp.length / ratio);
      const pcm = new Int16Array(n);
      for (let i = 0; i < n; i++) { const v = inp[Math.floor(i * ratio)]!; pcm[i] = Math.max(-1, Math.min(1, v)) * 0x7fff; }
      ws.send(pcm.buffer);
    };
    src.connect(node); node.connect(ctx.destination);
    ws.onmessage = (e) => {
      if (typeof e.data === 'string') {
        const m = JSON.parse(e.data) as { type: string; peer?: boolean; on?: boolean; reason?: string };
        note(`relay ${m.type}${m.peer !== undefined ? ` peer=${m.peer}` : ''}${m.on !== undefined ? ` on=${m.on}` : ''}${m.reason ? ` ${m.reason}` : ''}`);
        if ((m.type === 'joined' && m.peer) || (m.type === 'peer' && m.on)) { setError(null); goLive(); sendDiag(); }
        if (m.type === 'peer' && m.on === false) setPhase((p) => (p === 'active' ? 'reconnecting' : p));
        if (m.type === 'refused' || m.type === 'error') { setError('The call could not connect. Call again, or keep chatting.'); void hangUp(); }
        return;
      }
      if (++heard === 1) note('relay audio arriving');
      const pcm = new Int16Array(e.data as ArrayBuffer);
      const buf = ctx.createBuffer(1, pcm.length, 16000);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i]! / 0x8000;
      const play = ctx.createBufferSource();
      play.buffer = buf;
      play.connect(ctx.destination); play.connect(out);
      // A small buffer smooths network jitter; if we fall far behind, catch up.
      const now = ctx.currentTime;
      if (r.next < now + 0.04 || r.next > now + 0.6) r.next = now + 0.12;
      play.start(r.next);
      r.next += buf.duration;
    };
    ws.onclose = () => { if (relay.current === r && callId.current) setPhase((p) => (p === 'active' ? 'reconnecting' : p)); };
    remote.current = out.stream;
  }
  function goLive() {
    setPhase('active');
    if (!timer.current) timer.current = window.setInterval(() => setSeconds((x) => x + 1), 1000);
    if (!recorder.current) startRecording();
  }
  function stopRelay() {
    if (relayTimer.current) { clearTimeout(relayTimer.current); relayTimer.current = null; }
    const r = relay.current; relay.current = null;
    if (!r) return;
    try { r.ws.close(); } catch { /* closed */ }
    try { r.node.disconnect(); r.src.disconnect(); } catch { /* gone */ }
    void r.ctx.close().catch(() => undefined);
  }
  /** Give the direct route a few seconds after both sides have joined, then fall back to the relay. */
  function armRelay() {
    if (!api.relayUrl || relayTimer.current || relay.current) return;
    note('waiting for direct route');
    relayTimer.current = window.setTimeout(() => { relayTimer.current = null; if (pc.current?.connectionState !== 'connected') void startRelay(); }, 4500);
  }

  const cleanup = useCallback(async (upload = true) => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    const id = callId.current;
    const rec = recorder.current;
    if (rec && rec.state !== 'inactive') {
      await new Promise<void>((done) => { rec.onstop = () => done(); rec.stop(); });
      if (upload && id && api.saveRecording && chunks.current.length) {
        const blob = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' });
        await api.saveRecording(id, blob).catch(() => undefined);
      }
    }
    stopRelay();
    recorder.current = null; chunks.current = [];
    void mix.current?.close().catch(() => undefined); mix.current = null;
    pc.current?.close(); pc.current = null;
    local.current?.getTracks().forEach((t) => t.stop()); local.current = null;
    remote.current = null;
    if (audio.current) audio.current.srcObject = null;
    callId.current = null; iceSeen.current = 0; answered.current = false; pendingIce.current = []; mutedRef.current = false; log.current = [];
    setPhase('idle'); setSeconds(0); setMuted(false); setIncoming(null);
  }, [api]);

  function startRecording() {
    if (!api.saveRecording || !local.current || !remote.current || typeof MediaRecorder === 'undefined') return;
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      ctx.createMediaStreamSource(local.current).connect(dest);
      ctx.createMediaStreamSource(remote.current).connect(dest);
      const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t));
      const r = new MediaRecorder(dest.stream, type ? { mimeType: type } : undefined);
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.start(5000);
      recorder.current = r; mix.current = ctx;
    } catch { /* recording is best-effort; the call still works */ }
  }

  /** One hidden audio element on the page plays the other person. Created on the tap so phones allow sound. */
  function speaker() {
    if (!audio.current) {
      const el = document.createElement('audio');
      el.autoplay = true; el.setAttribute('playsinline', ''); el.style.display = 'none';
      document.body.appendChild(el);
      audio.current = el;
    }
    return audio.current;
  }
  const relayed = useRef(false);
  const servers_count = useRef(0);

  async function peer() {
    const servers = await api.iceServers();
    servers_count.current = servers.length;
    relayed.current = servers.some((x) => [].concat(x.urls as never).some((u: string) => /^turns?:/.test(u)));
    const conn = new RTCPeerConnection({ iceServers: servers, bundlePolicy: 'max-bundle' });
    local.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    local.current.getTracks().forEach((t) => conn.addTrack(t, local.current!));
    // Candidates found before the server has given the call an id wait in a queue, so none are lost.
    conn.onicecandidate = (e) => {
      if (!e.candidate) return;
      if (callId.current) void api.ice(callId.current, e.candidate.toJSON()).catch(() => undefined);
      else pendingIce.current.push(e.candidate.toJSON());
    };
    conn.ontrack = (e) => {
      remote.current = e.streams[0] ?? new MediaStream([e.track]);
      const el = speaker();
      el.srcObject = remote.current;
      void el.play().catch(() => undefined);
    };
    let drop: number | null = null;
    conn.oniceconnectionstatechange = () => note(`ice ${conn.iceConnectionState}`);
    conn.onicegatheringstatechange = () => note(`gathering ${conn.iceGatheringState}`);
    conn.onconnectionstatechange = () => {
      const st = conn.connectionState;
      note(`connection ${st}`);
      if (st === 'connected') void conn.getStats().then((r) => { r.forEach((x) => { if (x.type === 'candidate-pair' && (x as { state?: string }).state === 'succeeded' && (x as { nominated?: boolean }).nominated) { const l = r.get((x as { localCandidateId: string }).localCandidateId) as { candidateType?: string } | undefined; const rm = r.get((x as { remoteCandidateId: string }).remoteCandidateId) as { candidateType?: string } | undefined; note(`route ${l?.candidateType}->${rm?.candidateType}`); } }); sendDiag(); }).catch(() => undefined);
      if (st === 'connected') {
        if (drop) { clearTimeout(drop); drop = null; }
        if (relayTimer.current) { clearTimeout(relayTimer.current); relayTimer.current = null; }
        setError(null);
        goLive();
      }
      // A short network blip: keep the call and wait for the route to come back.
      if (st === 'disconnected') {
        setPhase((p) => (p === 'active' ? 'reconnecting' : p));
        if (!drop) drop = window.setTimeout(() => { if (conn.connectionState !== 'connected') { if (api.relayUrl) void startRelay(); else { setError('The connection was lost. Call again, or keep chatting.'); void hangUp(); } } }, 5000);
      }
      if (st === 'failed') {
        if (api.relayUrl) { void startRelay(); return; }
        setError(relayed.current ? 'The call dropped. Check your internet connection and call again.' : 'The call could not connect on this network. Try Wi-Fi, or keep chatting while we fix it.');
        void hangUp();
      }
    };
    pc.current = conn;
    return conn;
  }

  /** Wait briefly for the browser to find its network routes, so the first message already carries them. */
  const gathered = (conn: RTCPeerConnection) => new Promise<void>((done) => {
    if (conn.iceGatheringState === 'complete') return done();
    const t = window.setTimeout(done, 3000);
    conn.addEventListener('icegatheringstatechange', () => { if (conn.iceGatheringState === 'complete') { clearTimeout(t); done(); } });
  });
  const flushIce = () => { const id = callId.current; if (!id) return; for (const c of pendingIce.current.splice(0)) void api.ice(id, c).catch(() => undefined); };

  async function syncRemote(info: CallInfo) {
    if (['ended', 'missed', 'declined'].includes(info.status)) { note(`other side: ${info.status}`); sendDiag(); await cleanup(); return; }
    if (relay.current) return;
    const conn = pc.current;
    if (!conn) { if (info.relay && info.status === 'active' && callId.current) void startRelay(); return; }
    if (info.relay && !relay.current && info.status === 'active') { void startRelay(); return; }
    if (info.answer && !answered.current && conn.signalingState === 'have-local-offer') {
      answered.current = true;
      await conn.setRemoteDescription({ type: 'answer', sdp: info.answer });
      setPhase((p) => (p === 'calling' ? 'connecting' : p));
      armRelay();
    }
    if (conn.remoteDescription) {
      const fresh = info.remoteIce.slice(iceSeen.current);
      iceSeen.current = info.remoteIce.length;
      for (const c of fresh) await conn.addIceCandidate(c).catch(() => undefined);
    }
    if (['ended', 'missed', 'declined'].includes(info.status)) await cleanup();
  }

  // Watch for incoming calls while idle; follow the call while one is live.
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const tick = async () => {
      try {
        if (callId.current) await syncRemote(await api.get(callId.current));
        else {
          const c = await api.current();
          if (!stop) setIncoming(c && c.status === 'ringing' && c.startedBy !== api.side ? c : null);
          if (!stop && c && c.status === 'ringing' && c.startedBy !== api.side) setPhase((p) => (p === 'idle' ? 'incoming' : p));
          if (!stop && !c) setPhase((p) => (p === 'incoming' ? 'idle' : p));
        }
      } catch { /* keep polling */ }
    };
    void tick();
    const t = window.setInterval(tick, phase === 'idle' || phase === 'incoming' ? 3000 : phase === 'active' ? 2000 : 700);
    return () => { stop = true; clearInterval(t); };
  }, [enabled, phase]);

  async function call() {
    setError(null);
    void speaker().play().catch(() => undefined);
    try {
      setPhase('calling');
      const conn = await peer();
      const offer = await conn.createOffer({ offerToReceiveAudio: true });
      await conn.setLocalDescription(offer);
      await gathered(conn);
      const { id } = await api.start(conn.localDescription?.sdp ?? offer.sdp!);
      callId.current = id;
      note(`call started, ${servers_count.current} ice server groups`);
      flushIce();
    } catch (e) {
      note(`call error ${(e as Error).name}: ${(e as Error).message}`); sendDiag();
      setError(e instanceof DOMException && e.name === 'NotAllowedError' ? 'Allow microphone access in your browser to call.' : e instanceof Error ? e.message : 'Could not start the call.');
      await cleanup(false);
    }
  }

  async function accept() {
    const c = incoming;
    if (!c) return;
    setError(null);
    void speaker().play().catch(() => undefined);
    try {
      setPhase('connecting');
      callId.current = c.id;
      const conn = await peer();
      await conn.setRemoteDescription({ type: 'offer', sdp: c.offer });
      const ans = await conn.createAnswer();
      await conn.setLocalDescription(ans);
      await gathered(conn);
      await api.answer(c.id, conn.localDescription?.sdp ?? ans.sdp!);
      answered.current = true;
      note(`answered, ${servers_count.current} ice server groups`);
      armRelay();
      await syncRemote(await api.get(c.id));
    } catch (e) {
      note(`answer error ${(e as Error).name}: ${(e as Error).message}`); sendDiag();
      setError(e instanceof DOMException && e.name === 'NotAllowedError' ? 'Allow microphone access in your browser to answer.' : e instanceof Error ? e.message : 'Could not answer.');
      await cleanup(false);
    }
  }

  async function hangUp() {
    note('hang up'); sendDiag();
    const id = callId.current ?? incoming?.id;
    if (id) await api.end(id).catch(() => undefined);
    await cleanup();
  }

  function toggleMute() {
    const next = !muted;
    local.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    mutedRef.current = next;
    setMuted(next);
  }

  // Ring loudly while a call is coming in.
  useEffect(() => { if (phase !== 'incoming') return; return startRinging(2500); }, [phase]);
  useEffect(() => () => { void cleanup(); }, []);
  return { phase, error, muted, seconds, incoming, call, accept, hangUp, toggleMute };
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Call controls: a call button when idle, answer and decline when ringing, mute and hang up in a call. */
export function CallBar({ v, who, canCall = true, recorded = true }: { v: ReturnType<typeof useVoiceCall>; who: string; canCall?: boolean; recorded?: boolean }) {
  return (
    <div className={`callbar ${v.phase}`}>
      {v.phase === 'idle' && canCall && <button type="button" className="btn ghost sm" onClick={() => void v.call()}><Phone />Call {who}</button>}
      {v.phase === 'incoming' && <>
        <span className="call-ring"><PhoneIncoming />{v.incoming?.startedBy === 'staff' ? `${v.incoming?.staffName ?? 'CK support'} is calling you` : 'The client is calling'}</span>
        <button type="button" className="btn sm call-ok" onClick={() => void v.accept()}><Phone />Answer</button>
        <button type="button" className="btn ghost sm" onClick={() => void v.hangUp()}><PhoneOff />Decline</button>
      </>}
      {(v.phase === 'calling' || v.phase === 'connecting') && <>
        <span className="call-ring"><Phone />{v.phase === 'calling' ? `Calling ${who}…` : 'Connecting…'}</span>
        <button type="button" className="btn ghost sm" onClick={() => void v.hangUp()}><PhoneOff />Cancel</button>
      </>}
      {v.phase === 'reconnecting' && <span className="call-ring"><Phone />Reconnecting…</span>}
      {(v.phase === 'active' || v.phase === 'reconnecting') && <>
        {v.phase === 'active' && <span className="call-live"><i />On call · {clock(v.seconds)}{recorded ? ' · recorded' : ''}</span>}
        <button type="button" className="btn ghost sm" onClick={v.toggleMute}>{v.muted ? <MicOff /> : <Mic />}{v.muted ? 'Unmute' : 'Mute'}</button>
        <button type="button" className="btn sm call-end" onClick={() => void v.hangUp()}><PhoneOff />Hang up</button>
      </>}
      {v.error && <span className="call-err">{v.error}</span>}
    </div>
  );
}
